import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getVenues } from "@/api/admin.api";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Pagination } from "@/components/pagination";
import { Search } from "lucide-react";

const planBadgeClass: Record<string, string> = {
  FREE: "bg-gray-100 text-gray-700",
  PRO: "bg-blue-100 text-blue-700",
  BUSINESS: "bg-purple-100 text-purple-700",
};

export function VenuesPage() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [planType, setPlanType] = useState<string>("");
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "venues", page, search, planType],
    queryFn: () =>
      getVenues({
        page,
        limit: 20,
        search: search || undefined,
        planType: planType || undefined,
      }),
  });

  return (
    <div>
      <h1 className="mb-6 text-2xl font-bold">Isletmeler</h1>

      <div className="mb-4 flex gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Isim veya slug ile ara..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-10"
          />
        </div>
        <Select
          value={planType}
          onValueChange={(v) => {
            setPlanType(v === "ALL" ? "" : (v ?? ""));
            setPage(1);
          }}
        >
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Plan" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">Tumu</SelectItem>
            <SelectItem value="FREE">FREE</SelectItem>
            <SelectItem value="PRO">PRO</SelectItem>
            <SelectItem value="BUSINESS">BUSINESS</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Isim</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Sahip</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Urunler</TableHead>
              <TableHead>Durum</TableHead>
              <TableHead>Olusturulma</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                  Yukleniyor...
                </TableCell>
              </TableRow>
            ) : data?.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-8 text-center text-muted-foreground">
                  Isletme bulunamadi
                </TableCell>
              </TableRow>
            ) : (
              data?.data.map((venue) => (
                <TableRow
                  key={venue._id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => navigate(`/venues/${venue._id}`)}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {venue.logo ? (
                        <img src={venue.logo} alt="" className="size-8 rounded-lg object-cover" />
                      ) : (
                        <div className="flex size-8 items-center justify-center rounded-lg bg-muted text-xs font-medium">
                          {venue.name[0]?.toUpperCase()}
                        </div>
                      )}
                      <span className="font-medium">{venue.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">/{venue.slug}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {venue.owner?.name ?? "—"}
                  </TableCell>
                  <TableCell>
                    <Badge className={planBadgeClass[venue.planType] ?? ""}>
                      {venue.planType}
                    </Badge>
                  </TableCell>
                  <TableCell>{venue._count?.items ?? 0}</TableCell>
                  <TableCell>
                    {venue.isActive ? (
                      <Badge className="bg-green-100 text-green-700">Aktif</Badge>
                    ) : (
                      <Badge variant="secondary">Pasif</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(venue.createdAt).toLocaleDateString("tr-TR")}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {data && (
        <Pagination page={page} totalPages={data.totalPages} onPageChange={setPage} />
      )}
    </div>
  );
}
